/**
 * A Super Admin's "complete the pending steps": takes an order that was never packed on to Packed,
 * Invoiced, Dispatched or Delivered through each step's own endpoint, in lifecycle order — so stock, the
 * invoice, the ledger and the dispatch record follow exactly as when people take the steps one by
 * one. Nothing is decided here: Warehouse judges every reading, Billing raises the invoice, Ordering
 * refuses whatever its state machine refuses.
 *
 * Every run starts from what the order is now, so a run stopped by a refusal resumes where it
 * stopped: a step already taken is skipped, never repeated.
 */
import {
  approveOrder,
  assignDispatcher,
  confirmThirdPartyDelivered,
  decideShortFulfilment,
  deliverInHouse,
  dispatchInHouse,
  getDispatchView,
  getOrder,
  handOverToThirdParty,
  packRecorded,
  setDeliveryType,
  type DeliveryType,
  type OrderDetail,
} from "./services/ordering";
import { completePackingTask, packingTaskForOrder, syncPackReadings, type PackingTaskLineRow, type PackReading, type SyncOutcome } from "./services/warehouse";
import { finaliseInvoice, listInvoices } from "./services/invoicing";
import { saveTransport, type TransportRecord } from "./services/dispatch";

export type CompleteTarget = "packed" | "invoiced" | "dispatched" | "delivered";

export const isCompleteTarget = (value: string | null): value is CompleteTarget =>
  value === "packed" || value === "invoiced" || value === "dispatched" || value === "delivered";

/**
 * The states this form can carry an order on from. Before them sit a payment or a company's own
 * approval, which no one here can give; a cancelled order goes nowhere.
 */
export const canCompleteFrom = (state: string) =>
  ["awaiting_approval", "approved", "picking_packing", "packed", "invoiced", "dispatched"].includes(state);

type Measured = Pick<PackReading, "actualWeight" | "actualPieceCount" | "actualDimension" | "standardSizeConfirmed">;

/**
 * Only a line ordered in kg is weighed at the pack (owner, 2026-10-28); the ordered unit decides, not
 * the stocking unit. Anything ordered in pieces or feet packs as ordered unless the person says it was
 * packed less: the ordered count at a standard size on a counted line, with its nominal weight where
 * Warehouse cross-checks one (a SKU stocked in kg); the expected stocking-unit quantity otherwise.
 * Null when the line has to be asked for after all — ordered in kg, or no expected figure to pack it at.
 */
export function packedAsOrdered(line: PackingTaskLineRow): Measured | null {
  // ponytail: "ordered in kg" = stocked by weight and converting 1:1, so a non-kg unit weighing exactly
  // 1 kg would be asked too. Warehouse sending the ordered unit's type would make it exact.
  if (line.stockedByWeight && !line.isCountBased && line.conversionToStocking === 1) return null;
  if (line.isCountBased) {
    return line.weightCrossCheckRequired && line.expectedWeight === null
      ? null
      : { actualWeight: line.weightCrossCheckRequired ? line.expectedWeight : null, actualPieceCount: line.orderedQty, actualDimension: null, standardSizeConfirmed: true };
  }
  return line.expectedWeight === null ? null : { actualWeight: line.expectedWeight, actualPieceCount: null, actualDimension: null, standardSizeConfirmed: false };
}

export type CompleteInput = {
  target: CompleteTarget;
  actorId: string;
  /** One per packing line; a line Warehouse already holds a live reading for is not sent again. */
  readings: PackReading[];
  /** Why a short pack ships short. Asked for only once Warehouse reports one. */
  shipShortReason: string;
  deliveryType: DeliveryType;
  /** In-House: who takes it out and records the delivery. */
  dispatcherId: string | null;
  /** In-House: Billing's transport record, saved before the order leaves. Third-party asks for none. */
  transport: TransportRecord;
  receiverName: string;
  /** The signed challan or signature, as an image data URI. */
  signature: string;
  /** Per order line delivered short of what was invoiced: how much, and why. Absent = all of it. */
  shortDelivered: Record<string, { quantity: number; reason: string }>;
};

export type CompleteResult =
  | { stop: "done" }
  /** Warehouse refused or held some readings; the rest were recorded. */
  | { stop: "pack"; outcome: SyncOutcome }
  /** Warehouse reported a short pack, which needs a ship-short reason before it can be invoiced. */
  | { stop: "short" }
  /** Billing has not raised the invoice yet. */
  | { stop: "invoice" };

// ponytail: fixed 10 × 1 s. Warehouse's and Billing's posts reach Ordering on their own time; a
// longer wait just ends in `stop: "invoice"` and the next run picks up from there.
const TRIES = 10;

async function poll<T>(read: () => Promise<T>, ready: (value: T) => boolean): Promise<T> {
  for (let attempt = 1; ; attempt++) {
    const value = await read();
    if (ready(value) || attempt === TRIES) return value;
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }
}

export async function completeSteps(
  orderId: string,
  input: CompleteInput,
  /** Called with the lifecycle state each step is heading for, as it starts. */
  onStep: (state: string) => void = () => {},
): Promise<CompleteResult> {
  let order: OrderDetail = await getOrder(orderId);

  if (order.state === "awaiting_approval") {
    onStep("approved");
    await approveOrder(orderId);
  }

  if (!packRecorded(order)) {
    onStep("packed");
    const task = await packingTaskForOrder(orderId);
    if (!task) throw new Error("This order has no packing task, so it cannot be packed. One opens when the order is approved.");
    if (task.status !== "complete") {
      const packed = new Set(task.entries.filter((entry) => entry.isLive).map((entry) => entry.orderLineId));
      const readings = input.readings.filter((reading) => !packed.has(reading.orderLineId));
      if (readings.length > 0) {
        const outcome = await syncPackReadings(task.id, input.actorId, readings);
        if (outcome.rejected.length > 0 || outcome.conflicts.length > 0) return { stop: "pack", outcome };
      }
      await completePackingTask(task.id);
    }
    order = await poll(() => getOrder(orderId), packRecorded);
  }
  // Packed is the pack itself; a short pack's decision and the invoice Billing raises from it follow
  // on the order page.
  if (input.target === "packed") return { stop: "done" };

  if (order.shortFulfilmentPending) {
    if (!input.shipShortReason) return { stop: "short" };
    onStep("packed");
    await decideShortFulfilment(orderId, "ship_short", input.shipShortReason);
  }

  if (!order.invoiceId) {
    onStep("invoiced");
    // D-40: Billing raises a draft from the pack; finalising releases it and Ordering moves to Invoiced.
    const draft = await poll(
      () => listInvoices({ page: 1, pageSize: 1, orderIds: [orderId] }).then((page) => page.items[0] ?? null),
      (row) => row !== null,
    );
    if (!draft) return { stop: "invoice" };
    if (draft.reviewPending) await finaliseInvoice(draft.id);
    order = await poll(() => getOrder(orderId), (o) => o.invoiceId !== null);
    if (!order.invoiceId) return { stop: "invoice" };
  }
  if (input.target === "invoiced") return { stop: "done" };

  const type = (order.deliveryType as DeliveryType | null | undefined) ?? input.deliveryType;
  if (order.state === "invoiced") {
    onStep("dispatched");
    if (order.deliveryType !== type) await setDeliveryType(orderId, type);
    if (type === "third_party") {
      await handOverToThirdParty(orderId);
    } else {
      if (input.dispatcherId) await assignDispatcher(orderId, input.dispatcherId);
      await saveTransport(order.invoiceId, input.transport);
      await dispatchInHouse(orderId);
    }
    order = await getOrder(orderId);
  }
  if (input.target === "dispatched") return { stop: "done" };

  if (order.state === "dispatched") {
    onStep("delivered");
    if (type === "third_party") {
      await confirmThirdPartyDelivered(orderId);
    } else {
      // Delivered against what Billing invoiced, line by line — the figure is the API's, never derived.
      const { lines } = await getDispatchView(orderId);
      await deliverInHouse(orderId, {
        receiverName: input.receiverName,
        signature: input.signature,
        lines: lines
          .filter((line) => line.invoicedQuantity !== null)
          .map((line) => {
            const short = input.shortDelivered[line.orderLineId];
            return {
              orderLineId: line.orderLineId,
              deliveredQuantity: short ? short.quantity : line.invoicedQuantity!,
              shortageReason: short ? short.reason : null,
            };
          }),
      });
    }
  }
  return { stop: "done" };
}
